import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SidebarLayout from '../../components/SidebarLayout';
import './styles.css';
import { API_BASE_URL as API_URL } from '../../config/apiBase';


const VisualsPage = () => {
  const navigate = useNavigate();
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState('');
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [file, setFile] = useState(null);
  const [schoolInfo, setSchoolInfo] = useState(null);

  useEffect(() => {
    fetchSchemas();
    fetchSchoolInfo();
  }, []);

  const fetchSchemas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/visuals/schemas`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch schemas');
      }

      const data = await response.json();
      setSchemas(data.data || []);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchSchoolInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/visuals/entity`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch school information');
      }

      const data = await response.json();
      setSchoolInfo(data.data);
    } catch (err) {
      console.error('Failed to fetch school info:', err.message);
      // Don't set error state for school info as it's not critical
    }
  };

  const fetchTableData = async (schemaId) => {
    if (!schemaId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/visuals/data?schema_id=${schemaId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch table data');
      }

      const data = await response.json();
      setTableData(data.data.records || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSchemaChange = (e) => {
    const schemaId = e.target.value;
    setSelectedSchema(schemaId);
    if (schemaId) {
      fetchTableData(schemaId);
    } else {
      setTableData([]);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['.csv', '.xlsx', '.xls'];
      const fileExt = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(fileExt)) {
        setError('Please select a CSV or Excel file');
        return;
      }

      if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
        setError('File size must be less than 10MB');
        return;
      }

      setFile(selectedFile);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedSchema) {
      setError('Please select a file and schema');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('schema_id', selectedSchema);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/visuals/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Upload failed');
      }

      setSuccess(`Successfully uploaded ${data.insertedCount} records`);
      setFile(null);
      document.getElementById('file-input').value = '';
      
      // Refresh table data
      if (selectedSchema) {
        fetchTableData(selectedSchema);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const getTableColumns = () => {
    if (tableData.length === 0) return [];
    
    const firstRecord = tableData[0];
    if (firstRecord && firstRecord.record_data) {
      return Object.keys(firstRecord.record_data);
    }
    return [];
  };

  const renderTableData = () => {
    const columns = getTableColumns();
    
    if (columns.length === 0) {
      return (
        <div className="visuals-empty-state">
          No data available for the selected schema
        </div>
      );
    }

    return (
      <div className="visuals-table-container">
        <table className="visuals-table">
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th key={index}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map((record, index) => (
              <tr key={record.record_id || index}>
                {columns.map((column, colIndex) => (
                  <td key={colIndex}>
                    {record.record_data[column] || '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <SidebarLayout>
      <div className="visuals-container">
        {/* Removed back button - sidebar handles navigation */}

      <div className="visuals-content">
        {/* Quick Actions Section */}
        <div className="visuals-section">
          <h2>Quick Actions</h2>
          <div className="visuals-actions">
            <button 
              onClick={() => navigate('/visuals/create-table')}
              className="visuals-action-btn create-table-btn"
            >
              <span className="action-icon">📊</span>
              <div className="action-content">
                <h3>Create New Table</h3>
                <p>Define a custom table structure for your data</p>
              </div>
            </button>
            <button 
              onClick={() => navigate('/visuals/tables')}
              className="visuals-action-btn manage-tables-btn"
            >
              <span className="action-icon">🗂️</span>
              <div className="action-content">
                <h3>Manage Tables</h3>
                <p>View, add data, and analyze your existing tables</p>
              </div>
            </button>
          </div>
        </div>

        {/* Upload Section */}
        <div className="visuals-section">
          <h2>Upload Data</h2>
          <div className="visuals-upload-form">
            <div className="visuals-form-group">
              <label>Select Schema:</label>
              <select 
                value={selectedSchema} 
                onChange={handleSchemaChange}
                className="visuals-select"
              >
                <option value="">Choose a schema...</option>
                {schemas.map((schema) => (
                  <option key={schema.schema_id} value={schema.schema_id}>
                    {schema.table_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="visuals-form-group">
              <label>Select File:</label>
              <input
                id="file-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="visuals-file-input"
              />
            </div>

            <button
              onClick={handleUpload}
              disabled={!file || !selectedSchema || uploading}
              className="visuals-upload-btn"
            >
              {uploading ? 'Uploading...' : 'Upload File'}
            </button>
          </div>

          {error && (
            <div className="visuals-error">
              {error}
            </div>
          )}

          {success && (
            <div className="visuals-success">
              {success}
            </div>
          )}
        </div>

        {/* Data View Section */}
        <div className="visuals-section">
          <h2>View Data</h2>
          <div className="visuals-form-group">
            <label>Select Table to View:</label>
            <select 
              value={selectedSchema} 
              onChange={handleSchemaChange}
              className="visuals-select"
            >
              <option value="">Choose a table...</option>
              {schemas.map((schema) => (
                <option key={schema.schema_id} value={schema.schema_id}>
                  {schema.table_name}
                </option>
              ))}
            </select>
          </div>

          {loading && (
            <div className="visuals-loading">
              Loading data...
            </div>
          )}

          {!loading && selectedSchema && (
            <div className="visuals-data-section">
              {tableData.length > 0 ? (
                <>
                  <div className="visuals-data-info">
                    Showing {tableData.length} records
                  </div>
                  {renderTableData()}
                </>
              ) : (
                <div className="visuals-empty-state">
                  No data found for the selected table
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </div>
    </SidebarLayout>
  );
};

export default VisualsPage;

